// Reconocer numeros de telefono dentro de un texto, para poder llamarlos con
// un clic.
//
// Lissette, 2026-08-26: *"donde salga algun numero telefonico, como en cliente
// y en company information, que uno pueda marcar/llamar directo dandole clic
// al numero"*.
//
// El campo de telefonos de Informacion de Companias es TEXTO LIBRE y suele
// llevar varios numeros con su etiqueta ("Ventas: 800-555-1234", salto de
// linea, "Reclamos: 800-555-9999"). Asi que no vale con envolver el campo
// entero: hay que encontrar cada numero DENTRO del texto y dejar el resto tal
// cual.
//
// EL RIESGO, y por que las reglas de abajo son estrictas: pasarse marcando.
// Un numero de poliza convertido en enlace de llamada hace que el agente
// marque a un desconocido — y encima creyendo que llama a la aseguradora. Eso
// es peor que no tener la funcion. Dos defensas:
//
//   1. Esto se aplica SOLO en campos que ya son de telefono (la casilla de
//      telefonos de una compania, el telefono de la ficha). Nunca sobre texto
//      cualquiera, donde un numero de diez digitos puede ser cualquier cosa.
//   2. Un candidato tiene que tener una cantidad de digitos que de verdad
//      marque: 10, u 11 empezando por 1, o internacional con `+`.
//
// Vive en su propio modulo, sin dependencias, para poder probarlo desde
// `npm test` del backend: los .jsx no se pueden importar fuera de un navegador.

// Trozos que PODRIAN ser un telefono: empiezan y acaban en digito, y por el
// medio admiten los separadores de siempre. La validacion de verdad viene
// despues, contando digitos.
const CANDIDATO = /\+?\d[\d\s().\-–]{6,22}\d/g;

/**
 * El numero listo para `tel:`, o null si el candidato no marca.
 *
 * @param {string} bruto  El trozo tal cual aparece en el texto.
 * @returns {string|null}
 */
export function telHref(bruto) {
  const s = String(bruto == null ? '' : bruto);
  const masInternacional = s.trim().startsWith('+');
  const digitos = s.replace(/\D/g, '');

  if (masInternacional) {
    // Rango de la norma E.164. Por debajo de 8 no es un numero de verdad; por
    // encima de 15 no existe.
    if (digitos.length < 8 || digitos.length > 15) return null;
    return `+${digitos}`;
  }
  // Estados Unidos: diez digitos, u once si lleva el 1 delante. Cualquier otra
  // longitud se rechaza — ahi es donde caen las fechas (ocho digitos), los
  // codigos postales y los numeros de poliza cortos.
  if (digitos.length === 10) return digitos;
  if (digitos.length === 11 && digitos.startsWith('1')) return digitos;
  return null;
}

/**
 * El numero listo para un enlace `https://wa.me/<esto>`, o null si no marca.
 *
 * Se apoya en `telHref` a proposito, en vez de limpiar el texto por su cuenta:
 * asi lo que se acepta como telefono y lo que se acepta como WhatsApp es lo
 * MISMO. Un numero que no pasa el filtro devuelve null y quien llama lo pinta
 * como texto sin enlace — mejor un dato visible sin enlace que un enlace que
 * abre una conversacion con un numero equivocado.
 *
 * La diferencia con `tel:` es el prefijo de pais: wa.me lo exige SIEMPRE y sin
 * el '+'. `telHref` solo devuelve el '+' cuando el agente lo escribio, y los
 * diez digitos pelados de un numero de EE.UU. cuando no; por eso a esos —y
 * solo a esos— se les pone el 1 delante.
 *
 * @param {string} bruto
 * @returns {string|null}
 */
export function waHref(bruto) {
  const tel = telHref(bruto);
  if (!tel) return null;
  if (tel.startsWith('+')) return tel.slice(1);
  return tel.length === 10 ? '1' + tel : tel;
}

/**
 * Parte un texto en trozos, marcando cuales son telefonos.
 *
 * @param {string} texto
 * @returns {Array<{ text: string, tel: string|null }>}
 *   `tel` no nulo = ese trozo se pinta como enlace de llamada. El texto de
 *   alrededor (las etiquetas, los saltos de linea, las extensiones) vuelve
 *   intacto, para que la casilla siga leyendose igual que antes.
 */
export function splitPhoneParts(texto) {
  const s = String(texto == null ? '' : texto);
  if (!s) return [];

  const partes = [];
  let desde = 0;
  // `CANDIDATO` es global: se reinicia en cada llamada para que dos usos
  // seguidos no se salten la mitad de los numeros.
  CANDIDATO.lastIndex = 0;
  let m;
  while ((m = CANDIDATO.exec(s)) !== null) {
    const bruto = m[0];
    const tel = telHref(bruto);
    if (!tel) continue; // no marca: se queda como texto normal
    if (m.index > desde) partes.push({ text: s.slice(desde, m.index), tel: null });
    partes.push({ text: bruto, tel });
    desde = m.index + bruto.length;
  }
  if (desde < s.length) partes.push({ text: s.slice(desde), tel: null });
  return partes;
}
